import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Boxes, Plus, Pencil, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mfgAPI } from '../../services/api';
import { toast } from 'sonner';

const EMPTY = { document_type: '', description: '', prefix: '', from_number: 1000000, to_number: 9999999, current_number: 1000000, number_length: 10 };

const NumberRanges = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try { setLoading(true); const res = await mfgAPI.getNumberRanges(); setRows(res.data); }
    catch (e) { toast.error('Failed to load number ranges'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const submit = async () => {
    if (!form.document_type) { toast.error('Document type is required'); return; }
    try {
      if (editing) {
        await mfgAPI.updateNumberRange(editing.document_type, {
          description: form.description, current_number: Number(form.current_number), to_number: Number(form.to_number),
        });
        toast.success('Range updated');
      } else {
        await mfgAPI.createNumberRange({
          document_type: form.document_type.toUpperCase(), description: form.description, prefix: form.prefix || null,
          from_number: Number(form.from_number), to_number: Number(form.to_number),
          current_number: Number(form.current_number), number_length: Number(form.number_length),
        });
        toast.success('Range created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="mfg-number-ranges-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing')}><ChevronLeft className="h-5 w-5" /></Button>
            <div className="p-2 bg-orange-600 rounded-lg"><Boxes className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Manufacturing Number Ranges</h1>
              <p className="text-sm text-slate-500">Document numbering for PRD / BOM / RTG / CNF</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="create-range-btn"><Plus className="h-4 w-4 mr-2" /> Add Range</Button>
        </div>

        <Card className="border-0 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold">Document Type</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Prefix</TableHead>
                <TableHead className="font-semibold text-right">From</TableHead>
                <TableHead className="font-semibold text-right">To</TableHead>
                <TableHead className="font-semibold text-right">Current</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No ranges configured (auto-created on first document)</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.document_type} data-testid={`range-row-${r.document_type}`} className="hover:bg-orange-50/40">
                  <TableCell className="font-mono font-medium text-orange-600">{r.document_type}</TableCell>
                  <TableCell>{r.description || '—'}</TableCell>
                  <TableCell>{r.prefix || '—'}</TableCell>
                  <TableCell className="text-right">{r.from_number}</TableCell>
                  <TableCell className="text-right">{r.to_number}</TableCell>
                  <TableCell className="text-right font-semibold">{r.current_number}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`edit-range-${r.document_type}`}><Pencil className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? `Edit Range ${editing.document_type}` : 'Create Number Range'}</DialogTitle>
              <DialogDescription>Configure document numbering for manufacturing documents (PRD / BOM / RTG / CNF).</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Document Type *</Label>
                <Input data-testid="range-doctype-input" value={form.document_type} disabled={!!editing} onChange={(e) => set('document_type', e.target.value.toUpperCase())} maxLength={10} placeholder="PRD, BOM, RTG, CNF" />
              </div>
              <div>
                <Label>Prefix</Label>
                <Input data-testid="range-prefix-input" value={form.prefix} disabled={!!editing} onChange={(e) => set('prefix', e.target.value.toUpperCase())} maxLength={4} />
              </div>
              <div>
                <Label>From Number</Label>
                <Input type="number" value={form.from_number} disabled={!!editing} onChange={(e) => set('from_number', e.target.value)} />
              </div>
              <div>
                <Label>To Number</Label>
                <Input type="number" value={form.to_number} onChange={(e) => set('to_number', e.target.value)} />
              </div>
              <div>
                <Label>Current Number</Label>
                <Input type="number" data-testid="range-current-input" value={form.current_number} onChange={(e) => set('current_number', e.target.value)} />
              </div>
              <div>
                <Label>Number Length</Label>
                <Input type="number" value={form.number_length} disabled={!!editing} onChange={(e) => set('number_length', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => set('description', e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-orange-600 hover:bg-orange-700" data-testid="save-range-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default NumberRanges;
